import styled from "styled-components";
import { staticText } from "../../shared/statics/static-text";
import {
  maxXSmall,
  shade0,
  shade300,
  shade700,
  shade900,
  shade950,
} from "../../styles/tokens";

// TEXT
export const text = staticText.pagination;

// STYLES
export const PageButton = styled.button<PageButtonProps>`
  background-color: ${({ $selected }) => ($selected ? shade0 : "transparent")};
  color: ${({ $selected }) => ($selected ? shade900 : "inherit")};
  cursor: ${({ $selected }) => ($selected ? "default" : "pointer")};

  &[disabled],
  &:disabled {
    color: ${shade300};
    background-color: ${({ $selected }) => ($selected ? shade700 : "inherit")};
    cursor: default;
  }

  &:hover:not([disabled]) {
    background-color: ${({ $selected }) => ($selected ? shade0 : shade900)};
    color: ${({ $selected }) => ($selected ? shade900 : shade0)};
  }

  &:focus-visible:not([disabled]) {
    box-shadow: inset 0 0 0 1px ${shade0};
    background-color: ${shade950};
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

export const PageButtonsContainer = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  > button {
    width: 2rem;
    height: 2rem;
    border-radius: 100%;
  }

  @media ${maxXSmall} {
    gap: 0.25rem;
  }
`;

// DATA

// TYPES
export type Props = {
  /** The max amount of page buttons to display before truncating with ... */
  displayLimit?: number;
  /** The max amount of data items to show per page */
  pageSize: number;
  /** The total amount of data items */
  totalRecords: number;
  /** Callback function triggered whenever a page button is clicked */
  onPageClick: (index: number) => void;
  /** Optionally disable all pagination buttons */
  disabled?: boolean;
  /** Text footer below the pagination buttons */
  footer?: boolean;
  /** The page index that is currently active */
  pageIndex: number;
  /** Hide the pagination if there is only one page */
  hideSinglePage?: boolean;
  /** Always show first element */
  alwaysShowFirstPageNumber?: boolean;
  /** Always show last element */
  alwaysShowLastNumber?: boolean;
  /** Show first three dots on last page only, otherwise show it on every page*/
  showFirstThreeDotsOnLastPageOnly?: boolean;
};

type PageButtonProps = {
  $selected: boolean;
};

export type SkeletonProps = Pick<Props, "hideSinglePage" | "footer">;

// CONSTANTS

// FUNCTIONS
export const getPaginationRange = ({
  limit,
  pageIndex,
  totalPages,
}: {
  limit: number;
  pageIndex: number;
  totalPages: number;
}): [number, number] => {
  /** If a display limit exists, this sets the first clickable page index to be the current page index minus half of the display limit. If that value is below zero, this sets the first clickable page index to zero. If there's no display limit at all, this always sets the first clickable page index to 0 */
  let startPage = limit ? Math.max(pageIndex - Math.floor(limit / 2), 0) : 0;
  /** If a display limit doesn't exist, this sets the last clickable page index equal to the total amount of pages. Otherwise, this sets it equal to the first clickable page index plus the value of the display limit */
  let endPage = limit ? startPage + limit : totalPages;
  /** If current page index reaches the last few pages and the last clickable page index surpasses the total pages value, this freezes the last clickable page index so that it equals the total pages value and subtracts that difference from the first clickable page index so that it freezes as well */
  if (endPage > totalPages) {
    startPage -= endPage - totalPages;
    endPage = totalPages;
  }
  /** If the current page index is at the first few pages and the first clickable page index is less than zero, this freezes the first clickable page index so that it's equal to 0, and adds that difference to the last clickable page index so that it freezes as well */
  if (startPage < 0) {
    endPage += Math.abs(startPage);
    startPage = 0;
  }
  return [startPage, endPage];
};

export const getFooterText = (
  pageIndex: number,
  pageSize: number,
  totalRecords: number,
) => {
  return text.footerText
    .replace(
      "{startResult}",
      totalRecords ? String(pageIndex * pageSize + 1) : "0",
    )
    .replace(
      "{endResult}",
      String(Math.min(pageIndex * pageSize + pageSize, totalRecords)),
    )
    .replace("{totalResults}", String(totalRecords))
    .replace("{s}", totalRecords === 1 ? "" : "s");
};
